import React from "react"
import styled, { css, keyframes } from "styled-components"

import { maxWidth, minWidth, when, eq } from "utils"

import Icon from "icons"
import ContentfulGrid from "components/page-components/grid"
import ContentfulCard from "components/page-components/card"

export const NavSection = styled.nav`
  position: sticky;
  top: 0;
  z-index: 6;
  width: 100%;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none; // Let clicks pass through

  // Actual nav elements should receive clicks
  & > *,
  .MenuOverlay {
    pointer-events: auto;
  }

  /* Only apply transform on mobile */
  ${maxWidth.mobile`
    transform: translateY(${({ $hideNavOnScroll }) => ($hideNavOnScroll ? "-100%" : "0")});
  `}

  ${when("hide")`
    display: none;
  `}

  ${maxWidth.mobile`
    ${when("hideOnMobile")`
      display: none;
    `}
  `}
`

export const MainNav = styled.div`
  position: relative;
  height: 8rem;
  padding: 2rem 4rem;
  border-bottom: ${({ isTranslucent }) => (isTranslucent ? "none" : "0.01rem solid #f1f1f1")};
  background-color: ${({ backgroundColor, isTranslucent }) =>
    backgroundColor || (isTranslucent ? "rgba(0, 0, 0, 0.1)" : "#fff")};
  color: ${({ fontColor }) => fontColor || "#222"};
  transition: background-color 0.25s ease-in-out, color 0.25s ease-in-out;
  z-index: 6;

  ${maxWidth.mobileM`
    padding: 0 2rem !important;
  `}

  ${maxWidth.mobile`
    height: calc(5.3rem + 3.3rem);
    padding: 0 3rem;
    border-bottom: ${({ isTranslucent }) => (isTranslucent ? "none" : "0.05rem solid #BFBFBF")};

  `}// This is hiding the main nav on desktop scroll
  /* ${when("hideMainNav")`
    visibility: hidden;
  `} */
`

export const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${maxWidth.mobile`
    height: 5.3rem;
  `}
`

export const Left = styled.div`
  height: 3.5rem;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

export const CloseIconButton = styled.button`
  display: none;

  ${eq("hide", false)`
    ${minWidth.mobile`
      display: block;
    `}
  `}
`

export const CloseIcon = styled(Icon)`
  color: #222;
`

export const Envelope = styled.img`
  margin-top: 0.3rem;
`

export const Logo = styled.a`
  display: inline-block;
  font-family: Monoline, "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 2.8rem;

  ::after {
    content: "Mindbodygreen";
    color: #222;
    ${({ isTranslucent, fontColor }) => isTranslucent && `color: ${fontColor || "#fff"};`}
  }

  ${maxWidth.mobile`
    font-size: 2.6rem;
  `}

  ${when("hide")`
    ${minWidth.mobile`
      display: none;
    `}
  `}
`

export const Right = styled.div`
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;
  gap: 2.8rem;

  svg {
    ${minWidth.tablet`
      vertical-align: bottom;
    `}
  }
`

export const DesktopWrapper = styled.div`
  display: none;

  ${minWidth.tabletLow`
    display: flex;
    gap: 2.8rem;
  `}
`

export const AccountSection = styled.div`
  margin-right: 0;
  display: flex;
  align-items: center;
  margin-left: 0;
  margin-right: 0;
  gap: 1.6rem;
`

export const SearchModalButton = styled.button`
  /* IE 11 */
  svg {
    max-width: 2.3rem;
  }

  ${maxWidth.mobile`
    display: flex;
  `}
`

export const MobileWrapper = styled.div`
  display: flex;

  ${minWidth.tabletLow`
    display: none;
  `}
`

export const Separator = styled.div`
  position: absolute;
  top: 5.3rem;
  left: 0;
  right: 0;
  border-top: 0.05rem solid #bfbfbf;
`

export const BottomRow = styled.div`
  width: 100%;
  height: 3.3rem;
  display: flex;
  align-items: center;
`

export const MobileTabs = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

export const Tab = styled.button`
  padding: 0.3rem 0.2rem 0.2rem 0.4rem;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.01rem;
  text-transform: uppercase;
  color: #222;
  height: 2rem;

  ${when("active")`
    color: #2260B2;
  `}

  ${when("selected")`
    color: #2260B2;
  `}

  ${({ $isDivider }) =>
    $isDivider &&
    css`
      pointer-events: none;
      color: #ccc;
      font-weight: 300;
      padding: 0;
      letter-spacing: 0;
      text-transform: none;
    `}

  ${minWidth.tabletLow`
    font-size: 1.6rem;
    letter-spacing: 0.2rem;
    height: 3rem;
  `}

  ${({ $isShop }) =>
    $isShop &&
    css`
      font-weight: 700;
      border-left: 0.5px solid rgba(0, 0, 0, 0.25);
      padding-left: 2.5rem;

      ${minWidth.mobileM`
        padding-left: 4rem;
      `}

      @media (min-width: 425px) {
        padding-left: 5rem;
      }

      ${minWidth.tabletLow`
        border-left: 0.5px solid #000;
        border-right: 0.5px solid #000;
        padding: 0 2rem;
      `}
    `}
`

export const ArrowIcon = styled(Icon)`
  color: #222;

  ${eq("$menuOpen", false)`
    color: #F0F0F0;
    transform: rotate(180deg);
  `}
`
const menuSlideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const menuSlideUp = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

export const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  max-width: 144rem;
  margin: 0 auto;
  height: 100vh;
  background-color: rgba(200, 200, 200, 0.5);
  backdrop-filter: blur(0.8rem);
  visibility: ${({ $menuOpen }) => ($menuOpen ? "visible" : "hidden")};
  animation: ${({ $menuOpen }) =>
    $menuOpen
      ? css`
          ${fadeIn} 0.6s ease-out forwards
        `
      : css`
          ${fadeOut} 0.6s ease-out forwards
        `};
  animation-fill-mode: forwards;

  ${maxWidth.hamburgerLarge`
    display: none;
  `}
`

export const DesktopMenu = styled.div`
  width: 100%;
  padding: 2.7rem 4rem 6.3rem 8.5rem;
  display: none;
  justify-content: space-between;
  background-color: #f6f6f6;
  visibility: hidden;

  ${minWidth.hamburgerLarge`
    display: flex;
    position: absolute;
    top: 100%;
    height: 80vh;
    gap: 3rem;
    visibility: ${({ $menuOpen }) => ($menuOpen ? "visible" : "hidden")};
    transition: visibility 0.3s ease-out;
    animation: ${({ $menuOpen }) =>
      $menuOpen
        ? css`
            ${menuSlideDown} 0.6s cubic-bezier(0.4, 0, 0.2, 1)
          `
        : css`
            ${menuSlideUp} 0.3s cubic-bezier(0.4, 0, 0.2, 1)
          `};
    animation-fill-mode: forwards;
    will-change: transform, opacity, height;
  `}
`

export const Items = styled.div`
  display: flex;
  flex-direction: column;
`

export const Item = styled.button`
  width: fit-content;
  margin-top: 5rem;
  padding: 0.3rem 0.2rem 0.2rem 0.4rem;
  font-size: 1.6rem;
  font-weight: 500;
  letter-spacing: 0.2rem;
  text-align: left;
  text-transform: uppercase;
  color: #222;

  ${when("selected")`
    background-color: ${({ theme }) => theme.colors.primaryBlue};
    color: #fff;
  `}
`

export const StyledGrid = styled(ContentfulGrid)`
  ${minWidth.hamburgerLarge`
    max-width: 100rem;
    padding: 0;
    flex: 1;
    max-height: 100%;
    overflow-y: auto;
    
    ::-webkit-scrollbar {
      display: none;
    }
  `}
`

export const MobileMenu = styled.div`
  position: relative;
  height: 100vh;
  height: 100svh;
  background-color: #fff;
  padding: 2.3rem 0 16rem 2rem;
  overflow-y: auto;

  ${minWidth.hamburgerLarge`
    display: none;
  `}
`

export const MobileGrids = styled.div``

export const TitleRow = styled.div`
  padding-right: 2rem;
  margin: 1.5rem 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  /* Remove margin top only for the first title */
  &:first-of-type {
    margin-top: 0;
  }
`

export const Title = styled.h2`
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.62rem;
  letter-spacing: 0.2rem;
  text-transform: uppercase;
  color: #222;
`

export const ScrollArrowLine = styled(Icon)`
  padding-right: 1rem;
  padding-bottom: 1rem;
  color: rgba(0, 0, 0, 0.33);
`

const ScrollArrowLineMobile = styled(ScrollArrowLine)`
  ${minWidth.mobile`
    display: none;
  `}
`

const ScrollArrowLineDesktop = styled(ScrollArrowLine)`
  display: none;

  ${minWidth.mobile`
    display: block;
  `}
`

export const ScrollArrow = () => (
  <>
    <ScrollArrowLineMobile viewBox="0 0 348 12" id="arrow-line-mobile" />
    <ScrollArrowLineDesktop viewBox="0 0 823 12" id="arrow-line-desktop" />
  </>
)

export const ScrollContainer = styled.div`
  width: 100%;
`

export const ScrollableGrid = styled.div`
  height: 17rem;
  display: -webkit-box;
  overflow-x: auto;
  scroll-behavior: smooth;
`

export const MobileCard = styled(ContentfulCard)`
  width: 20rem;
  height: 15.4rem;
`
